/**
 * Helpers for errors thrown by Amplify `API.graphql`.
 *
 * Amplify rejects GraphQL and network failures with a plain object
 * `{ data, errors: [{ message, errorType }] }` rather than an `Error`, so
 * `error.message` is undefined and callers that read it fall back to generic text.
 */

/** Extract a human-readable message from Amplify/AppSync GraphQL errors. */
export const formatGraphqlError = (err: unknown): string => {
  if (err instanceof Error && err.message && err.message !== '[object Object]') {
    return err.message;
  }
  if (err && typeof err === 'object') {
    const o = err as Record<string, unknown>;
    if (typeof o.message === 'string' && o.message) {
      return o.message;
    }
    if (Array.isArray(o.errors) && o.errors.length > 0) {
      const first = o.errors[0];
      if (first && typeof first === 'object' && typeof (first as { message?: string }).message === 'string') {
        return (first as { message: string }).message;
      }
    }
    try {
      return JSON.stringify(err);
    } catch {
      return String(err);
    }
  }
  return String(err);
};

/**
 * True when AppSync rejected a conditional write because the DynamoDB condition
 * (e.g. `updatedAt eq <last read>`) no longer held — the row changed since it was read.
 */
export const isConditionalCheckFailedError = (err: unknown): boolean => {
  if (!err || typeof err !== 'object') return false;
  const { errors } = err as { errors?: unknown };
  if (!Array.isArray(errors)) return false;
  return errors.some((entry) => (
    Boolean(entry)
    && typeof entry === 'object'
    && (entry as { errorType?: unknown }).errorType === 'DynamoDB:ConditionalCheckFailedException'
  ));
};
